import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import DetailScaffold from "../detail/components/DetailScaffold";
import Gallery from "../detail/components/Gallery";
import ShareButton from "../detail/components/ShareButton";
import {
  AvailabilityBanner,
  QuantityStepper,
  SpecList,
  StarRating,
  SupplierAvatar,
} from "../detail/components/DetailBits";
import { CART_PATH, supplierPath } from "../detail/detailRoutes";
import useDetailOrigin from "../detail/useDetailOrigin";
import useAddToCart from "../detail/useAddToCart";
import useCart from "../cart/useCart";
import { getAuthToken } from "../../lib/api/tokenStore";
import { showErrorToast } from "../../components/Toast";

import { AccessoryDetailSkeleton } from "./components/AccessorySkeletons";
import AccessoryEmptyState from "./components/AccessoryEmptyState";
import { emptyState } from "./accessoryCopy";
import { formatAmount } from "./accessoryMoney";
import useAccessoryDetail from "./useAccessoryDetail";

import "./styles.css";

const MAX_QUANTITY = 99;

const AccessoryDetailRoute = () => {
  const navigate = useNavigate();
  const { productId } = useParams();
  const origin = useDetailOrigin();

  const detail = useAccessoryDetail({
    productId: productId ? Number(productId) : null,
    coordinates: origin.coordinates,
  });

  const cart = useCart({ enabled: Boolean(getAuthToken()) });
  const { busyAction, addToCart, conflictDialog } = useAddToCart({ cart });

  const [quantity, setQuantity] = useState(1);

  const line = useMemo(
    () => ({
      productId: detail.product?.id ?? null,
      containerMaterialId: detail.materialId ?? null,
      volume: null,
      variableVolume: false,
      supplierId: detail.supplier?.id ?? null,
    }),
    [detail.product, detail.materialId, detail.supplier]
  );

  const cartQuantity = useMemo(() => {
    if (!line.productId) return 0;

    return (
      cart.basket.find(
        (row) =>
          row.productId === line.productId &&
          (row.containerMaterialId ?? null) === (line.containerMaterialId ?? null)
      )?.quantity ?? 0
    );
  }, [cart.basket, line]);

  useEffect(() => {
    setQuantity(cartQuantity > 0 ? cartQuantity : 1);
  }, [cartQuantity]);

  const blocked = Boolean(detail.notice) || !detail.inStock;

  const action =
    cartQuantity === 0 ? "add" : quantity === cartQuantity ? "view" : "update";

  const write = async (then) => {
    if (blocked) {
      showErrorToast(
        detail.notice
          ? detail.notice.message
          : "This accessory is out of stock in the selected material.",
        detail.supplier?.name
      );

      return;
    }

    const result = await addToCart({
      items: [{ ...line, quantity }],
      action: action === "update" ? "update" : "add",
      mode: "set",
    });

    if (result?.status === "added") then?.();
  };

  const primary = () => {
    if (action === "view") {
      navigate(CART_PATH);

      return;
    }

    write();
  };

  const buyNow = () => {
    if (action === "view") {
      navigate(CART_PATH);

      return;
    }

    write(() => navigate(CART_PATH));
  };

  if (detail.isError) {
    return (
      <div className="acc-detail">
        <AccessoryEmptyState
          state={emptyState({ isError: true })}
          onRetry={detail.refetch}
        />
      </div>
    );
  }

  const product = detail.product;
  const supplier = detail.supplier;

  const primaryLabel =
    action === "add" ? "Add to Cart" : action === "update" ? "Update Cart" : "View Cart";

  const lineTotal = Number.isFinite(detail.price?.value)
    ? detail.price.value * quantity
    : null;

  return (
    <DetailScaffold
      title={product?.name || "Accessory"}
      layout="split"
      heroAuto
      hero={
        <Gallery images={detail.images} alt={product?.name || "Accessory"} showThumbs />
      }
      full={
        product && (
          <section className="sy-product-info">
            <h2>About this product</h2>

            {product.description ? (
              <p className="sy-product-description">{product.description}</p>
            ) : (
              <p className="sy-product-description sy-muted">
                The supplier has not added a description for this item yet.
              </p>
            )}

            <SpecList items={detail.specs} />
          </section>
        )
      }
    >
      {detail.isLoading ? (
        <AccessoryDetailSkeleton />
      ) : (
        <section className="sy-buy">
          <div className="sy-buy-head">
            <h2 className="sy-buy-title">{product?.name}</h2>

            <div className="sy-buy-tools">
              <ShareButton title={product?.name || "Accessory"} />
            </div>
          </div>

          {detail.materialChips.length > 1 && (
            <ul
              className="sy-chip-row sy-chip-row-select"
              role="radiogroup"
              aria-label="Material"
            >
              {detail.materialChips.map((chip) => (
                <li key={chip.id}>
                  <button
                    type="button"
                    role="radio"
                    aria-checked={chip.id === detail.materialId}
                    className={`sy-chip sy-chip-select${
                      chip.id === detail.materialId ? " is-selected" : ""
                    }${chip.inStock ? "" : " is-out"}`}
                    onClick={() => detail.setMaterialId(chip.id)}
                  >
                    {chip.label}
                    {chip.price && (
                      <i>{` (${formatAmount(chip.price.value, chip.price.currency)})`}</i>
                    )}
                  </button>
                </li>
              ))}
            </ul>
          )}

          <div className="sy-buy-price">
            <b>{formatAmount(detail.price?.value, detail.price?.currency)}</b>

            {detail.discounted && (
              <s>
                {formatAmount(
                  detail.originalPrice.value,
                  detail.originalPrice.currency
                )}
              </s>
            )}
          </div>

          <p className="sy-buy-meta">
            <span className={`sy-stock${detail.inStock ? "" : " is-out"}`}>
              {detail.inStock ? "In stock" : "Out of stock"}
            </span>

            {quantity > 1 && lineTotal !== null && (
              <span>{`Total ${formatAmount(lineTotal, detail.price?.currency)}`}</span>
            )}
          </p>

          <AvailabilityBanner notice={detail.notice} />

          <div className="sy-buy-actions">
            <QuantityStepper
              value={quantity}
              max={MAX_QUANTITY}
              disabled={blocked}
              onChange={setQuantity}
            />

            {!detail.inStock ? (
              <button type="button" className="sy-btn sy-btn-primary" disabled>
                Out of Stock
              </button>
            ) : action === "view" ? (
              <button
                type="button"
                className="sy-btn sy-btn-primary"
                onClick={() => navigate(CART_PATH)}
              >
                View Cart
              </button>
            ) : (
              <>
                <button
                  type="button"
                  className="sy-btn sy-btn-outline"
                  disabled={blocked || Boolean(busyAction)}
                  onClick={primary}
                >
                  {busyAction ? "Working…" : primaryLabel}
                </button>

                <button
                  type="button"
                  className="sy-btn sy-btn-primary"
                  disabled={blocked || Boolean(busyAction)}
                  onClick={buyNow}
                >
                  {busyAction ? "Working…" : "Buy Now"}
                </button>
              </>
            )}
          </div>

          {supplier && (
            <div className="sy-seller">
              <SupplierAvatar name={supplier.name} logoUrl={supplier.logoUrl} size={44} />

              <div className="sy-seller-body">
                <span className="sy-seller-label">Sold by</span>
                <span className="sy-seller-name">{supplier.name}</span>

                <span className="sy-seller-meta">
                  {detail.rating.total > 0 && (
                    <StarRating rating={detail.rating.average} total={detail.rating.total} />
                  )}
                </span>
              </div>

              <button
                type="button"
                className="sy-seller-link"
                onClick={() =>
                  navigate(
                    supplierPath({
                      supplierId: supplier.id,
                      coordinates: origin.coordinates,
                    })
                  )
                }
              >
                Visit store
              </button>
            </div>
          )}
        </section>
      )}

      {conflictDialog}
    </DetailScaffold>
  );
};

export default AccessoryDetailRoute;
